import type { ReactNode } from "react";
import type { SvgIconComponent } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { cn } from "@/utils/cn";
import { Breadcrumbs, type BreadcrumbItem } from "./Breadcrumbs";
import { useActiveModule } from "@/hooks/useActiveModule";
import { resolvePageChromeBreadcrumbs } from "@/utils/breadcrumbs";
import { localizeBreadcrumbItems } from "@/i18n/navLabel";

interface PageHeaderProps {
  /**
   * @deprecated Headings are no longer shown. Kept for call-site compatibility.
   * Use browser/document title hooks if a tab title is needed.
   */
  title?: string;
  /** @deprecated Descriptions are no longer shown under the page chrome. */
  description?: ReactNode;
  /**
   * Optional trail override. Prefer omitting this so crumbs match nav/settings.
   * Entity detail pages may pass a trail whose last crumb has no `to` (appended).
   */
  breadcrumbs?: BreadcrumbItem[];
  /** Extra crumbs after the auto Module › Page trail (e.g. entity name). */
  breadcrumbTail?: BreadcrumbItem[];
  actions?: ReactNode;
  /** @deprecated Page title icons are no longer shown. */
  icon?: SvgIconComponent | null;
  /** @deprecated Page title icons are no longer shown. */
  showIcon?: boolean;
  className?: string;
}

export function PageHeader({
  breadcrumbs,
  breadcrumbTail,
  actions,
  className,
}: PageHeaderProps) {
  const { t } = useTranslation("navigation");
  const location = useLocation();
  const activeModule = useActiveModule();
  const rawBreadcrumbs = resolvePageChromeBreadcrumbs(location.pathname, {
    activeModule,
    hash: location.hash.replace(/^#/, "") || undefined,
    override: breadcrumbs,
    tail: breadcrumbTail,
  });
  const displayBreadcrumbs = localizeBreadcrumbItems(rawBreadcrumbs, t, location.pathname);

  if (displayBreadcrumbs.length === 0 && !actions) {
    return null;
  }

  return (
    <header className={cn("mb-4", className)}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        {displayBreadcrumbs.length > 0 ? (
          <Breadcrumbs items={displayBreadcrumbs} />
        ) : (
          <span className="min-w-0" />
        )}
        {actions ? (
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </div>
    </header>
  );
}
